import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { Users, Plus, Edit, Trash2, UserCheck, ArrowLeft, Eye, EyeOff, Star, Activity, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: 'employee' | 'manager' | 'admin';
  managers?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  // Keep legacy fields for backward compatibility
  managerId?: string;
  managerName?: string;
  directReports?: string[];
  createdAt: any;
  lastLogin?: any;
  isActive: boolean;
  upForReview?: boolean;
  reviewCycle?: string;
  upForPulseCheck?: boolean;
  isHR?: boolean;
  isSuperHR?: boolean;
}

const UserManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    role: 'employee' as 'employee' | 'manager' | 'admin',
    managerIds: [] as string[]
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      
      if (!currentUser) return;
      
      // Fetch from Firestore directly for now
      const { collection, getDocs } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      const members: TeamMember[] = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Handle both new multiple managers format and legacy single manager
        let managers: Array<{id: string, name: string, email: string}> = [];
        if (data.managers && Array.isArray(data.managers)) {
          managers = data.managers;
        } else if (data.managerId && data.managerName) {
          // Convert legacy single manager to new format
          const legacyManager = snapshot.docs.find(d => d.id === data.managerId);
          if (legacyManager) {
            managers = [{
              id: data.managerId,
              name: data.managerName,
              email: legacyManager.data().email || ''
            }];
          }
        }
        
        return {
          id: doc.id,
          email: data.email || '',
          displayName: data.displayName || data.email?.split('@')[0] || 'Unknown',
          photoURL: data.photoURL || undefined,
          role: data.role || 'employee',
          managers,
          // Keep legacy fields for backward compatibility
          managerId: data.managerId || undefined,
          managerName: data.managerName || undefined,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastLogin: data.lastLogin?.toDate() || undefined,
          directReports: [],
          upForReview: data.upForReview || false,
          reviewCycle: data.reviewCycle || undefined,
          upForPulseCheck: data.upForPulseCheck || false,
          isHR: data.isHR || false,
          isSuperHR: data.isSuperHR || false
        };
      });
      
      setTeamMembers(members);
    } catch (err: any) {
      console.error('Error fetching team members:', err);
      setError(err.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Validate email domain
      const allowedDomains = ['@stitchdesignco.com', '@sdcopartners.com'];
      const hasAllowedDomain = allowedDomains.some(domain => 
        formData.email.toLowerCase().endsWith(domain)
      );

      if (!hasAllowedDomain) {
        setError('Email must be from @stitchdesignco.com or @sdcopartners.com domain');
        return;
      }

      // Check if email already exists
      const existingMember = teamMembers.find(member => 
        member.email.toLowerCase() === formData.email.toLowerCase()
      );

      if (existingMember) {
        setError('A team member with this email already exists');
        return;
      }

      if (!currentUser) return;

      // Save to Firestore directly
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Build managers array from selected manager IDs
      const managers = formData.managerIds.map(managerId => {
        const manager = teamMembers.find(m => m.id === managerId);
        return manager ? {
          id: managerId,
          name: manager.displayName,
          email: manager.email
        } : null;
      }).filter(Boolean) as Array<{id: string, name: string, email: string}>;

      const newMemberData = {
        email: formData.email.toLowerCase(),
        displayName: formData.displayName || formData.email.split('@')[0],
        role: formData.role,
        managers: managers.length > 0 ? managers : [],
        // Flat list of manager UIDs — used by Firestore rules to let a manager
        // toggle review/pulse status on their direct reports.
        managerIds: managers.map(m => m.id),
        // Keep legacy fields for backward compatibility (use first manager if any)
        managerId: managers.length > 0 ? managers[0].id : null,
        managerName: managers.length > 0 ? managers[0].name : null,
        isActive: true,
        createdAt: serverTimestamp(),
        addedBy: currentUser.uid
      };

      const docRef = await addDoc(collection(db, 'users'), newMemberData);

      // Add to local state for immediate UI update
      const newMember: TeamMember = {
        id: docRef.id,
        email: newMemberData.email,
        displayName: newMemberData.displayName,
        role: newMemberData.role,
        managers: newMemberData.managers,
        managerId: newMemberData.managerId || undefined,
        managerName: newMemberData.managerName || undefined,
        isActive: true,
        createdAt: new Date(),
        directReports: []
      };

      setTeamMembers([...teamMembers, newMember]);
      setFormData({ email: '', displayName: '', role: 'employee', managerIds: [] });
      setShowAddForm(false);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to add team member');
    }
  };

  const handleUpdateMember = async (member: TeamMember) => {
    try {
      if (!currentUser) return;

      // Update in Firestore
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', member.id);
      const updateData = {
        displayName: member.displayName,
        role: member.role,
        managers: member.managers || [],
        // Flat list of manager UIDs — used by Firestore rules to let a manager
        // toggle review/pulse status on their direct reports.
        managerIds: (member.managers || []).map(m => m.id),
        // Keep legacy fields for backward compatibility (use first manager if any)
        managerId: member.managers && member.managers.length > 0 ? member.managers[0].id : null,
        managerName: member.managers && member.managers.length > 0 ? member.managers[0].name : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      };

      await updateDoc(userRef, updateData);

      // Update local state
      const updatedMembers = teamMembers.map(m => 
        m.id === member.id ? { ...member } : m
      );
      setTeamMembers(updatedMembers);
      setEditingMember(null);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update team member');
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) return;
    
    try {
      if (!currentUser) return;

      // Soft delete in Firestore
      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      await updateDoc(userRef, {
        isActive: false,
        deactivatedAt: serverTimestamp(),
        deactivatedBy: currentUser.uid
      });

      // Remove from local state
      const updatedMembers = teamMembers.filter(m => m.id !== memberId);
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to remove team member');
    }
  };

  const handleToggleReviewStatus = async (memberId: string, upForReview: boolean) => {
    try {
      if (!currentUser) return;

      // Update in Firestore
      const { doc, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      // Use full timestamp so we can properly compare against submission times
      const reviewCycleTimestamp = upForReview ? Timestamp.now() : null;
      
      await updateDoc(userRef, {
        upForReview: upForReview,
        reviewCycleStart: reviewCycleTimestamp, // Full timestamp for accurate comparison
        reviewCycle: upForReview ? new Date().toISOString().split('T')[0] : null, // Keep date string for display
        reviewStatusUpdatedAt: serverTimestamp(),
        reviewStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              upForReview: upForReview,
              reviewCycle: upForReview ? new Date().toISOString().split('T')[0] : undefined
            }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to update review status');
    }
  };

  const handleTogglePulseCheckStatus = async (memberId: string, upForPulseCheck: boolean) => {
    try {
      if (!currentUser) return;

      const { doc, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      const pulseCheckCycleTimestamp = upForPulseCheck ? Timestamp.now() : null;
      
      await updateDoc(userRef, {
        upForPulseCheck: upForPulseCheck,
        pulseCheckCycleStart: pulseCheckCycleTimestamp,
        pulseCheckStatusUpdatedAt: serverTimestamp(),
        pulseCheckStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { ...member, upForPulseCheck: upForPulseCheck }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      console.error('Error updating pulse check status:', err);
      setError(err.message || 'Failed to update pulse check status');
    }
  };

  const handleToggleHRStatus = async (memberId: string, isHR: boolean) => {
    try {
      if (!currentUser) return;

      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      
      await updateDoc(userRef, {
        isHR: isHR,
        hrStatusUpdatedAt: serverTimestamp(),
        hrStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { ...member, isHR: isHR }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      console.error('Error updating HR status:', err);
      setError(err.message || 'Failed to update HR status');
    }
  };

  const handleToggleSuperHRStatus = async (memberId: string, isSuperHR: boolean) => {
    try {
      if (!currentUser) return;

      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      
      await updateDoc(userRef, {
        isSuperHR: isSuperHR,
        superHRStatusUpdatedAt: serverTimestamp(),
        superHRStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { ...member, isSuperHR: isSuperHR }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      console.error('Error updating Super HR status:', err);
      setError(err.message || 'Failed to update Super HR status');
    }
  };

  const managers = teamMembers.filter(member => member.role === 'manager' || member.role === 'admin');
  const usersUpForReview = teamMembers.filter(member => member.upForReview);
  const eligibleForReview = teamMembers.filter(member => member.isActive); // Allow all active users including admins for testing

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
                <Users className="h-6 w-6" />
                User Management
              </h1>
              <p className="text-muted-foreground">Manage team members and assignments</p>
            </div>
          </div>
          <Button onClick={() => setShowAddForm(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Team Member
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Peer Review Stats */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Peer Review Status
            </CardTitle>
            <CardDescription>Manage who is currently up for peer review</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold text-primary">{usersUpForReview.length}</div>
                <p className="text-sm text-muted-foreground">Up for Peer Review</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">{eligibleForReview.length}</div>
                <p className="text-sm text-muted-foreground">Eligible Users</p>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <div className="text-2xl font-bold">{Math.round((usersUpForReview.length / Math.max(eligibleForReview.length, 1)) * 100)}%</div>
                <p className="text-sm text-muted-foreground">Coverage</p>
              </div>
            </div>
            {usersUpForReview.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Currently up for review:</p>
                <div className="flex flex-wrap gap-2">
                  {usersUpForReview.map(user => (
                    <span key={user.id} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      {user.displayName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Member Form */}
        {showAddForm && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Add New Team Member</CardTitle>
              <CardDescription>Add a team member and assign their manager</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john.doe@stitchdesignco.com"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="displayName" className="block text-sm font-medium">
                      Display Name
                    </label>
                    <Input
                      id="displayName"
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="role" className="block text-sm font-medium">
                      Role
                    </label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    >
                      <option value="employee">Employee</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">
                      Managers (Optional)
                    </label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-input rounded-md p-2 bg-background">
                      {managers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No managers available</p>
                      ) : (
                        managers.map(manager => (
                          <label key={manager.id} className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.managerIds.includes(manager.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    managerIds: [...formData.managerIds, manager.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    managerIds: formData.managerIds.filter(id => id !== manager.id)
                                  });
                                }
                              }}
                              className="rounded border-input"
                            />
                            <span className="text-sm">
                              {manager.displayName} ({manager.email})
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    {formData.managerIds.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {formData.managerIds.length} manager{formData.managerIds.length > 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Add Member
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Team Members List */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members ({teamMembers.length})</CardTitle>
            <CardDescription>Manage team members and their manager assignments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {teamMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    {member.photoURL ? (
                      <img 
                        src={member.photoURL} 
                        alt={member.displayName} 
                        className="w-12 h-12 rounded-full" 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"
                      style={{ display: member.photoURL ? 'none' : 'flex' }}
                    >
                      <span className="text-primary font-semibold text-lg">
                        {(member.displayName || member.email || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.displayName}</p>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          member.role === 'admin' ? 'bg-red-100 text-red-800' :
                          member.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                          'bg-secondary text-secondary-foreground'
                        }`}>
                          {member.role}
                        </span>
                        {member.upForReview && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                            <Star className="h-3 w-3" />
                            Up for Peer Review
                          </span>
                        )}
                        {member.isSuperHR && (
                          <span className="px-2 py-1 text-xs rounded-full bg-pink-100 text-pink-800 flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            Super HR
                          </span>
                        )}
                        {member.isHR && !member.isSuperHR && (
                          <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" />
                            HR
                          </span>
                        )}
                        {member.upForPulseCheck && (
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            Pulse Check Enabled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                      {member.managers && member.managers.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Reports to: {member.managers.map(m => m.name).join(', ')}
                        </p>
                      )}
                      {!member.managers && member.managerName && (
                        <p className="text-xs text-muted-foreground">
                          Reports to: {member.managerName}
                        </p>
                      )}
                      {member.upForReview && member.reviewCycle && (
                        <p className="text-xs text-muted-foreground">
                          Review cycle: {member.reviewCycle}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant={member.upForReview ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleReviewStatus(member.id, !member.upForReview)}
                      className="flex items-center gap-1"
                    >
                      {member.upForReview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {member.upForReview ? 'Remove from Peer Review' : 'Add to Peer Review'}
                    </Button>
                    <Button
                      variant={member.isHR ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleHRStatus(member.id, !member.isHR)}
                      className={`flex items-center gap-1 ${member.isHR ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                    >
                      <ShieldCheck className="h-3 w-3" />
                      {member.isHR ? 'Remove HR' : 'Assign HR'}
                    </Button>
                    <Button
                      variant={member.isSuperHR ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleSuperHRStatus(member.id, !member.isSuperHR)}
                      className={`flex items-center gap-1 ${member.isSuperHR ? 'bg-pink-600 hover:bg-pink-700' : ''}`}
                    >
                      <ShieldCheck className="h-3 w-3" />
                      {member.isSuperHR ? 'Remove Super HR' : 'Assign Super HR'}
                    </Button>
                    <Button
                      variant={member.upForPulseCheck ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTogglePulseCheckStatus(member.id, !member.upForPulseCheck)}
                      className="flex items-center gap-1"
                    >
                      <Activity className="h-3 w-3" />
                      {member.upForPulseCheck ? 'Disable Pulse Check' : 'Enable Pulse Check'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingMember(member)}
                      className="flex items-center gap-1"
                    >
                      <Edit className="h-3 w-3" />
                      Edit
                    </Button>
                    {member.id !== currentUser?.uid && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteMember(member.id)}
                        className="flex items-center gap-1 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Edit Member Modal */}
        {editingMember && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Edit Team Member</CardTitle>
                <CardDescription>Update member information and assignments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Display Name</label>
                    <Input
                      value={editingMember.displayName}
                      onChange={(e) => setEditingMember({ ...editingMember, displayName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Role</label>
                    <select
                      value={editingMember.role}
                      onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value as any })}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    >
                      <option value="employee">Employee</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Managers</label>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-input rounded-md p-2 bg-background">
                      {managers.filter(m => m.id !== editingMember.id).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No managers available</p>
                      ) : (
                        managers.filter(m => m.id !== editingMember.id).map(manager => {
                          const isSelected = editingMember.managers?.some(m => m.id === manager.id) || false;
                          return (
                            <label key={manager.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentManagers = editingMember.managers || [];
                                  let newManagers;
                                  
                                  if (e.target.checked) {
                                    // Add manager
                                    newManagers = [...currentManagers, {
                                      id: manager.id,
                                      name: manager.displayName,
                                      email: manager.email
                                    }];
                                  } else {
                                    // Remove manager
                                    newManagers = currentManagers.filter(m => m.id !== manager.id);
                                  }
                                  
                                  setEditingMember({
                                    ...editingMember,
                                    managers: newManagers,
                                    // Update legacy fields for backward compatibility
                                    managerId: newManagers.length > 0 ? newManagers[0].id : undefined,
                                    managerName: newManagers.length > 0 ? newManagers[0].name : undefined
                                  });
                                }}
                                className="rounded border-input"
                              />
                              <span className="text-sm">
                                {manager.displayName} ({manager.email})
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {editingMember.managers && editingMember.managers.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {editingMember.managers.length} manager{editingMember.managers.length > 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <Button onClick={() => handleUpdateMember(editingMember)} className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Update
                  </Button>
                  <Button variant="outline" onClick={() => setEditingMember(null)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserManagement;
